import * as babylon from "@babylonjs/core";

import type { FileRecord, IFileService } from "@/shared/domain/files";
import { downloadGltfDocument } from "@/shared/domain/urdf";

import { readArticulation, type Articulation } from "./articulation";
import { gltfUriSites, withRewrittenUris, type GltfUriSite } from "./gltfUris";
import { isRelativeAssetUri, resolveSiblingPath } from "./siblingPaths";

/**
 * The slice of `FileService` a `ModelLoader` needs.
 *
 * Narrow on purpose: it keeps the loader's dependency surface honest and lets
 * a test supply a small fake instead of the whole service.
 */
export type ModelFileService = Pick<
  IFileService,
  | "getFileRecord"
  | "getFileRecordByPath"
  | "getPresignedDownloadUrl"
  | "getFilesByRelativePath"
  | "getJsonFileContents"
>;

/**
 * A parsed model: its Babylon scene nodes, and the kinematics it declared.
 *
 * The two arrive together because they come from one parse. `articulation` is
 * absent for a model that declares none, which is most of them.
 */
export interface LoadedModel {
  container: babylon.AssetContainer;
  articulation?: Articulation;
}

/**
 * One posable copy of a model, live in the scene.
 *
 * `rootNodes` is what the file's own roots became — for a glTF that is Babylon's
 * synthetic `__root__`, which carries the glTF-to-Babylon handedness conversion
 * as its own rotation and scaling. Parent these to an anchor of your own and
 * place the anchor; writing TRS onto a root discards the conversion and lays the
 * model on its side.
 *
 * `dispose` removes this copy and nothing else — the shared template, and the
 * geometry and materials this copy borrows from it, survive.
 */
export interface ModelInstance {
  rootNodes: babylon.TransformNode[];
  /**
   * The kinematics the file declares, from the same parse the copy came from,
   * so the panel learns a model's joints without fetching the file again.
   * Absent for a model that declares none, which is most of them.
   */
  articulation?: Articulation;
  dispose(): void;
}

/**
 * The capability the 3D panel's systems depend on.
 *
 * `reloadStaleMeshes` calls `cloneNode` and `instantiate`, and the ingest
 * preview only `load`, so they take this rather than the concrete class —
 * which keeps their tests free of a real file service and of the glTF plugin.
 */
export interface ModelAssetLoader {
  load(
    fileId: string,
    scene: babylon.Scene,
    signal: AbortSignal,
  ): Promise<LoadedModel>;

  cloneNode(
    fileId: string,
    nodeName: string,
    scene: babylon.Scene,
    signal: AbortSignal,
  ): Promise<babylon.AbstractMesh>;

  instantiate(
    fileId: string,
    scene: babylon.Scene,
    signal: AbortSignal,
  ): Promise<ModelInstance>;

  /**
   * Resolves once nothing this loader started is still parsing into `scene`.
   *
   * Exists for teardown. A template load deliberately outlives the entity that
   * asked for it (see {@link ModelLoader.template}), so aborting an entity does
   * not stop Babylon from parsing a glTF — and parsing creates materials,
   * which pull in the environment BRDF texture and its GPU-side expansion. A
   * scene whose engine has already been released is a bad place for that to
   * land, so whoever releases the engine waits for this first.
   *
   * Optional: only the real loader has anything to wait for. A fake that
   * resolves its loads synchronously can leave it off.
   */
  whenSettled?(scene: babylon.Scene): Promise<void>;
}

/** Model file extensions this loader accepts. */
const GLTF_EXTENSIONS = [".gltf", ".glb"] as const;

type GltfExtension = (typeof GLTF_EXTENSIONS)[number];

/**
 * Everything Babylon needs to load a model, with the network already done.
 *
 * `source` is either a presigned URL (GLB) or an inline `data:`-prefixed
 * document (glTF); `pluginExtension` tells Babylon which plugin to dispatch
 * to rather than making it guess from a URL that carries query parameters.
 */
export interface ModelSource {
  source: string;
  pluginExtension: GltfExtension;
}

/**
 * Registers Babylon's glTF 2.0 scene-loader plugin, once per page.
 *
 * Deferred behind a dynamic import so the loader's bytes stay out of the
 * initial bundle — a user who never configures a model never downloads it.
 * `@babylonjs/loaders/glTF/2.0` transitively imports `glTFFileLoader`, whose
 * module scope calls `RegisterSceneLoaderPlugin`, and also installs the
 * `_CreateGLTF2Loader` factory that plugin dispatches to; importing either
 * one alone is not enough.
 */
let gltfLoaderRegistration: Promise<unknown> | undefined;

export function registerGltfLoader(): Promise<unknown> {
  gltfLoaderRegistration ??= import("@babylonjs/loaders/glTF/2.0");
  return gltfLoaderRegistration;
}

function extensionOf(relativePath: string): string {
  const lastDot = relativePath.lastIndexOf(".");
  return lastDot === -1 ? "" : relativePath.slice(lastDot).toLowerCase();
}

/**
 * Default size, in framebuffer pixels, for point cloud points.
 *
 * Babylon's own default is 1px, which reads as noise on a dense scan; 2px
 * makes a decimated LiDAR map read as a surface on the displays it was tried
 * on. Framebuffer pixels, so a high-DPR display renders it proportionally
 * smaller in CSS terms. Per-entity overrides live in the panel's
 * `pointSizeOverride` machinery; this is the template's — and so every
 * non-overriding entity's — size.
 */
export const DEFAULT_POINT_SIZE = 2;

/**
 * Puts a loaded document's point cloud primitives into their default dress.
 *
 * Babylon's glTF loader maps a `POINTS` primitive onto a PBR material in
 * point-list fill mode (`material.pointsCloud`) but leaves the size at the 1px
 * default; this sets {@link DEFAULT_POINT_SIZE}. `unlit` is also pinned:
 * Babylon already shades a point-fill material unlit when the mesh carries no
 * normals — the shape every converted scan has — so for those this only states
 * the intent, but a POINTS primitive that *does* carry normals would otherwise
 * light up, and baked vertex colors are authored to render as-is.
 *
 * Applied to the container's materials, which the template shares with every
 * clone and instantiation, so one call at load covers all of them — and why
 * no per-entity value may ever be written here. A no-op for documents without
 * point primitives.
 */
export function configurePointMaterials(
  container: babylon.AssetContainer,
): void {
  for (const material of container.materials) {
    if (!material.pointsCloud) {
      continue;
    }
    material.pointSize = DEFAULT_POINT_SIZE;
    // Every material the glTF loader constructs is a PBRMaterial, including
    // the no-material fallback; other kinds cannot reach this loader.
    if (material instanceof babylon.PBRMaterial) {
      material.unlit = true;
    }
  }
}

function isGltfExtension(extension: string): extension is GltfExtension {
  return (GLTF_EXTENSIONS as readonly string[]).includes(extension);
}

/**
 * Loads a mesh file out of a Roboto dataset into a Babylon scene.
 *
 * Knows nothing about the 3D panel's entity-component state: it takes a file
 * id and a scene and hands back an `AssetContainer`, leaving placement,
 * lifetime, and teardown to the caller. That keeps the awkward part — async
 * multi-file fetching — separable and testable on its own.
 *
 * ## Why a `.gltf` cannot simply be handed to Babylon
 *
 * A `.gltf` references its external buffers and textures by URI relative to
 * itself. Given the file's S3 presigned URL, Babylon would resolve
 * `sample_arm.bin` against that URL's path and request it without a
 * signature, which S3 rejects with a 403. So the document is downloaded here,
 * through the file service, and each relative URI is resolved to a sibling
 * file in the same dataset and rewritten to that sibling's own presigned URL.
 * The rewritten document is handed to Babylon inline. A `.glb` needs none of
 * this — it carries its geometry in one file — and takes the presigned URL
 * directly.
 */
export class ModelLoader implements ModelAssetLoader {
  #files: ModelFileService;

  /**
   * One parsed template per scene and file, shared by every entity drawing a node
   * out of it.
   *
   * An imported robot is a dozen entities over one file; loading it per entity
   * would fetch and parse the same asset a dozen times. The template is never
   * added to the scene — a caller takes what it needs from it, either one
   * node's geometry ({@link cloneNode}) or a whole copy ({@link instantiate}),
   * both of which share the template's vertex buffers rather than copying them.
   *
   * Keyed by scene as well as file because an `AssetContainer` belongs to the
   * scene it was parsed into. Weak on the scene so a disposed panel's templates
   * become collectable.
   */
  #templates = new WeakMap<babylon.Scene, Map<string, Promise<LoadedModel>>>();

  constructor(files: ModelFileService) {
    this.#files = files;
  }

  /**
   * Loads the model stored at `fileId` into `scene`.
   *
   * The returned container is *not* added to the scene: the caller decides
   * when its nodes become visible, and owns disposing it.
   *
   * The document's kinematics come back alongside it. They are read through the
   * loader's `onParsed` hook rather than from the text this class already parses
   * in `#gltfSource`, because that path exists only for `.gltf` — a `.glb` is
   * handed to Babylon as a URL and never parsed here. `onParsed` sees both, and
   * costs no extra fetch either way.
   *
   * @throws if the file is not a `.gltf`/`.glb`, if the document is
   * unparseable, or if an external asset it references is missing from the
   * dataset. Rejects with an `AbortError` if `signal` fires.
   */
  async load(
    fileId: string,
    scene: babylon.Scene,
    signal: AbortSignal,
  ): Promise<LoadedModel> {
    const { source, pluginExtension } = await this.resolveSource(
      fileId,
      signal,
    );

    await registerGltfLoader();
    signal.throwIfAborted();

    let document: Record<string, unknown> | undefined;
    const container = await babylon.LoadAssetContainerAsync(source, scene, {
      pluginExtension,
      pluginOptions: {
        gltf: {
          onParsed: (data) => {
            document = data.json as Record<string, unknown>;
          },
        },
      },
    });

    configurePointMaterials(container);

    return { container, articulation: readArticulation(document) };
  }

  /**
   * A drawable copy of one node's geometry from the model at `fileId`.
   *
   * The clone carries the node's **world scale**, accumulated from its glTF
   * ancestors, and nothing else: it sits at the origin unrotated, because the
   * entity that owns this mesh supplies position and orientation, and that is
   * what lets the hierarchy drive it. This is the `model_node` path — one node's
   * geometry drawn at an entity's own pose. A whole articulated file is drawn by
   * `instantiate` instead, which keeps the document's own node hierarchy.
   *
   * Scale alone is baked because the entity hierarchy composes translation and
   * rotation but not scale — and nothing animates scale, so accumulating it once
   * at load is equivalent. Baking rotation here too would apply it twice, once on
   * the mesh and once through the entity.
   *
   * @throws if the file holds no node called `nodeName`, or if that node has no
   * geometry of its own — a structural node is a legitimate glTF node but there
   * is nothing to draw for it.
   */
  async cloneNode(
    fileId: string,
    nodeName: string,
    scene: babylon.Scene,
    signal: AbortSignal,
  ): Promise<babylon.AbstractMesh> {
    const { container: template } = await this.#template(fileId, scene);
    signal.throwIfAborted();

    const source = template.meshes.find((mesh) => mesh.name === nodeName);
    if (!source) {
      // A glTF node with no geometry loads as a `TransformNode`, not a mesh, so
      // it is absent from `meshes` despite being a real node in the file. Saying
      // "no such node" for one would be actively misleading — this is what every
      // link in a URDF export is.
      const structural = template.transformNodes.some(
        (node) => node.name === nodeName,
      );
      throw new Error(
        structural
          ? `Node ${nodeName} has no geometry of its own`
          : `No node named ${nodeName} in this model`,
      );
    }
    if (source.getTotalVertices() === 0) {
      throw new Error(`Node ${nodeName} has no geometry of its own`);
    }

    // The template was never added to the scene, so its world matrices are not
    // maintained for it; compute them before reading the node's world transform.
    source.computeWorldMatrix(true);
    const scale = new babylon.Vector3();
    source
      .getWorldMatrix()
      .decompose(
        scale,
        undefined /* rotation is the entity's */,
        undefined /* translation is the entity's */,
      );

    const clone = source.clone(
      `${nodeName}.clone`,
      null,
    ) as babylon.AbstractMesh;
    clone.parent = null;
    clone.position = babylon.Vector3.Zero();
    clone.rotationQuaternion = babylon.Quaternion.Identity();
    clone.scaling = scale;
    scene.addMesh(clone);
    return clone;
  }

  /**
   * A whole copy of the model at `fileId`, in the scene and ready to pose.
   *
   * This is what the template cache was for: one fetch, one parse, N copies
   * sharing geometry. The copy keeps the file's own node names and its own
   * hierarchy, so a caller can look a node up by name and write TRS on it.
   *
   * Cloned rather than instanced. Babylon's default is to emit an
   * `InstancedMesh` per mesh, which draws its *source* mesh's geometry at its
   * own transform — but the source here is the template, deliberately never
   * added to the scene. A clone shares the `Geometry` object just as an
   * instance shares the source's, so the memory is the same, and its transforms
   * are its own.
   *
   * One node kind still arrives as an `InstancedMesh`: where the *file* points
   * two nodes at one mesh, Babylon loads the second as an instance of the
   * first, and that relationship is replicated as it stands. Its source is the
   * copy's own clone rather than the template, so it too is in the scene, and
   * it holds its own transform like any other node.
   *
   * The returned `dispose` tears down this copy only; the template stays
   * cached, and so do the geometry and materials it lends out — instantiating
   * registers those with the scene, and disposing leaves them for the next
   * copy to reuse rather than adding more.
   */
  async instantiate(
    fileId: string,
    scene: babylon.Scene,
    signal: AbortSignal,
  ): Promise<ModelInstance> {
    const { container: template, articulation } = await this.#template(
      fileId,
      scene,
    );
    signal.throwIfAborted();

    const instance = template.instantiateModelsToScene(
      (name) => name,
      false /* materials are shared with the template, not copied */,
      { doNotInstantiate: true },
    );

    return {
      // Every node a model can hold — `Mesh`, `InstancedMesh`, `TransformNode`
      // — is a `TransformNode`, but `InstantiatedEntries` is typed for the
      // general case and declares the looser `Node`.
      rootNodes: instance.rootNodes as babylon.TransformNode[],
      ...(articulation ? { articulation } : {}),
      dispose: () => {
        instance.dispose();
      },
    };
  }

  /**
   * The shared, scene-bound template for a file, loading it on first use.
   *
   * The in-flight promise is cached, not just the result, so several entities
   * asking at once share one fetch. A failed load is evicted so a later attempt
   * can retry rather than replaying the error forever.
   */
  async #template(fileId: string, scene: babylon.Scene): Promise<LoadedModel> {
    let forScene = this.#templates.get(scene);
    if (!forScene) {
      forScene = new Map();
      this.#templates.set(scene, forScene);
    }

    const cached = forScene.get(fileId);
    if (cached) {
      return cached;
    }

    // Loaded with its own signal: the shared template must not be cancelled just
    // because the entity that happened to trigger it went away.
    const pending = this.load(
      fileId,
      scene,
      new AbortController().signal,
    ).catch((error: unknown) => {
      forScene?.delete(fileId);
      throw error;
    });
    forScene.set(fileId, pending);
    return pending;
  }

  /**
   * Resolves once nothing this loader started is still parsing into `scene`.
   * See {@link ModelAssetLoader.whenSettled}.
   *
   * Failures are settled outcomes too: a load that rejected has stopped
   * touching the scene, which is the only thing a caller waiting to tear the
   * scene down cares about. A template that fails also evicts itself, so the
   * `allSettled` can therefore be handed a promise the cache no longer holds
   * (it drains the iterator before any of them settle) — which is fine, that
   * is still the promise to wait on.
   */
  async whenSettled(scene: babylon.Scene): Promise<void> {
    const forScene = this.#templates.get(scene);
    if (!forScene) {
      return;
    }
    await Promise.allSettled(forScene.values());
  }

  /**
   * Resolves `fileId` to a Babylon scene source, performing every fetch the
   * model needs but no parsing into a scene.
   *
   * Separated from {@link load} because this is where all the behavior lives
   * — extension dispatch, URI rewriting, sibling resolution — and it can be
   * exercised without a WebGL context or a glTF plugin.
   */
  async resolveSource(
    fileId: string,
    signal: AbortSignal,
  ): Promise<ModelSource> {
    const record = await this.#files.getFileRecord(fileId, {
      abortSignal: signal,
    });
    signal.throwIfAborted();

    const pluginExtension = extensionOf(record.relative_path);
    if (!isGltfExtension(pluginExtension)) {
      throw new Error(
        `${record.relative_path} is not a glTF model (expected .gltf or .glb)`,
      );
    }

    const source =
      pluginExtension === ".glb"
        ? await this.#glbSource(record, signal)
        : await this.#gltfSource(record, signal);

    return { source, pluginExtension };
  }

  /**
   * A GLB is self-contained, so its presigned URL is the whole source.
   */
  async #glbSource(record: FileRecord, signal: AbortSignal): Promise<string> {
    const url = await this.#files.getPresignedDownloadUrl(record.file_id, {
      abortSignal: signal,
    });
    return url.toString();
  }

  /**
   * Downloads a `.gltf` through the file service, which retries a transient
   * failure, repoints its external URIs at presigned URLs, and returns it as
   * an inline Babylon scene source.
   *
   * The `"data:"` prefix is Babylon's documented form for handing over a
   * stringified scene rather than a URL to fetch. It is preferred over a
   * `File`, whose read path goes through `FileReader` — absent under Node,
   * which would confine every test of this method to a browser.
   */
  async #gltfSource(record: FileRecord, signal: AbortSignal): Promise<string> {
    const document = await downloadGltfDocument(this.#files, record, {
      abortSignal: signal,
    });
    signal.throwIfAborted();

    const replacements = await this.#resolveExternalAssets(
      record,
      gltfUriSites(document),
      signal,
    );

    return `data:${JSON.stringify(withRewrittenUris(document, replacements))}`;
  }

  /**
   * Maps each relative asset URI to a presigned URL for the sibling dataset
   * file it names.
   *
   * Inline (`data:`) and already-absolute URIs are passed over — they need no
   * dataset lookup and Babylon can consume them as they are.
   */
  async #resolveExternalAssets(
    record: FileRecord,
    sites: GltfUriSite[],
    signal: AbortSignal,
  ): Promise<GltfUriSite[]> {
    const relativeSites = sites.filter((site) => isRelativeAssetUri(site.uri));
    if (relativeSites.length === 0) {
      return [];
    }

    const resolved = relativeSites.map((site) => ({
      site,
      path: resolveSiblingPath(record.relative_path, site.uri),
    }));

    const unresolvable = resolved.filter(({ path }) => path === null);
    if (unresolvable.length > 0) {
      throw new Error(
        `${record.relative_path} references assets outside its dataset: ` +
          unresolvable.map(({ site }) => site.uri).join(", "),
      );
    }

    // De-duplicated: several sites can legitimately name the same file, and
    // one lookup plus one presign per distinct path is enough.
    const paths = [...new Set(resolved.map(({ path }) => path as string))];

    // Scoped to the model file's own org, not the org the user happens to be
    // browsing as. `HttpClient` carries the latter in a base header and the file
    // search is org-scoped, so a model in a public dataset — or in a second org
    // the user belongs to — would otherwise report every sibling missing. The
    // by-id routes above need no such scoping: they authorize per file.
    const found = new Map(
      await this.#files.getFilesByRelativePath(record.association_id, paths, {
        abortSignal: signal,
        resourceOwnerId: record.org_id,
      }),
    );
    signal.throwIfAborted();

    for (const [path, sibling] of await this.#resolveHiddenSiblings(
      record,
      paths.filter((path) => !found.has(path)),
      signal,
    )) {
      found.set(path, sibling);
    }

    const missing = paths.filter((path) => !found.has(path));
    if (missing.length > 0) {
      throw new Error(
        `${record.relative_path} is missing required assets: ${missing.join(", ")}`,
      );
    }

    // Presign concurrently — a textured model can reference many files, and
    // each presign is an independent round-trip.
    const presignedByPath = new Map(
      await Promise.all(
        paths.map(async (path) => {
          const sibling = found.get(path) as FileRecord;
          const url = await this.#files.getPresignedDownloadUrl(
            sibling.file_id,
            { abortSignal: signal },
          );
          return [path, url.toString()] as const;
        }),
      ),
    );

    return resolved.map(({ site, path }) => ({
      ...site,
      uri: presignedByPath.get(path as string) as string,
    }));
  }

  /**
   * Second pass for the paths the batch lookup could not see.
   *
   * `getFilesByRelativePath` goes through file search, which the service
   * prefilters to exclude every node whose name begins with a dot. That is what
   * keeps `.VISUALIZATION_ASSETS` out of search — and it also makes a generated
   * glTF's own `.bin` sibling, which lives right there beside it, invisible to
   * the batch call. The by-path record route applies no such filter, so anything
   * the batch missed gets one more chance through it.
   *
   * Deliberately a *fallback* and not the primary path: it costs one request per
   * path, and an ordinary visible model referencing a dozen textures should
   * still resolve them all in the single batched round-trip.
   *
   * A path that is missing for the ordinary reason — it really is not in the
   * dataset — 404s here, which is not an error worth propagating: the caller
   * reports every still-unresolved path together, naming all of them rather than
   * just whichever one happened to fail first.
   *
   * @returns the records recovered, as `[path, record]` pairs; misses are absent
   */
  async #resolveHiddenSiblings(
    record: FileRecord,
    paths: string[],
    signal: AbortSignal,
  ): Promise<(readonly [string, FileRecord])[]> {
    if (paths.length === 0) {
      return [];
    }

    const attempts = await Promise.all(
      paths.map(async (path) => {
        try {
          const sibling = await this.#files.getFileRecordByPath(
            record.association_id,
            path,
            { abortSignal: signal, resourceOwnerId: record.org_id },
          );
          return [path, sibling] as const;
        } catch {
          return [path, null] as const;
        }
      }),
    );
    // After the catch above, not before: an abort surfaces as a rejected lookup
    // that the catch swallows, and reporting the model as missing assets would
    // misrepresent a cancelled load.
    signal.throwIfAborted();

    return attempts.filter(
      (attempt): attempt is readonly [string, FileRecord] =>
        attempt[1] !== null,
    );
  }
}
